import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { BLOG, ARTICLE_ORDER, ARTICLE_IMAGES } from "@/i18n/blog";
import Header from "@/components/landing/Header";
import ContactFooter from "@/components/landing/ContactFooter";
import Seo from "@/components/Seo";
import { Overline } from "@/components/landing/primitives";

const SITE = "https://tuningfiles.hu";

const Blog = () => {
  const { lang } = useLang();
  const b = BLOG[lang];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: b.ui.title,
    url: `${SITE}/blog`,
    inLanguage: lang,
    blogPost: ARTICLE_ORDER.map((k) => ({
      "@type": "BlogPosting",
      headline: b.articles[k].title,
      datePublished: b.articles[k].date,
      url: `${SITE}/blog/${b.articles[k].slug}`,
    })),
  };

  return (
    <div className="relative min-h-screen bg-[#0A0A0A]" data-testid="blog-page">
      <Seo
        title={b.ui.meta.title}
        description={b.ui.meta.description}
        path="/blog"
        jsonLd={jsonLd}
      />
      <Header />

      <section className="relative overflow-hidden border-b border-white/10 px-5 pt-32 pb-16 md:px-10 md:pt-40 md:pb-20 grid-lines">
        <div className="pointer-events-none absolute -left-20 top-10 h-[340px] w-[340px] rounded-full bg-brand-green/10 blur-[150px]" />
        <div className="relative mx-auto max-w-[1400px]">
          <Overline>{b.ui.kicker}</Overline>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 max-w-4xl font-display text-5xl font-black leading-[0.95] text-white md:text-7xl"
          >
            {b.ui.title}
          </motion.h1>
          <p className="mt-6 max-w-2xl text-base text-white/60 md:text-lg">{b.ui.subtitle}</p>
        </div>
      </section>

      <section className="bg-[#0A0A0A] px-5 py-16 md:px-10 md:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-4 md:grid-cols-2">
          {ARTICLE_ORDER.map((k, i) => {
            const a = b.articles[k];
            return (
              <motion.div
                key={k}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.08 }}
              >
                <Link
                  to={`/blog/${a.slug}`}
                  className="group flex h-full flex-col overflow-hidden border border-white/10 bg-[#121212] transition-colors hover:border-brand-green"
                  data-testid={`blog-card-${k}`}
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={ARTICLE_IMAGES[k]}
                      alt={a.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                    <span className="absolute left-4 top-4 bg-brand-red px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white">
                      {a.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-widest text-white/40">
                      <span>{a.date}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {a.readTime} {b.ui.readTime}
                      </span>
                    </div>
                    <h2 className="mt-4 font-display text-2xl font-bold leading-tight text-white transition-colors group-hover:text-brand-green">
                      {a.title}
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-white/60">{a.excerpt}</p>
                    <span className="mt-auto flex items-center gap-2 pt-6 font-mono text-xs uppercase tracking-widest text-brand-green">
                      {b.ui.read}
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      <ContactFooter />
    </div>
  );
};

export default Blog;
